// All pages. Copy comes from content.js; layout pieces from components.jsx.
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Art, NetworkModel, productArt } from './illustrations';
import {
  usePageMeta, Section, Head, PageHero, Button, Ticks, Steps, ServiceCard,
  Person, Facts, PartnerBand, ContactForm,
} from './components';
import {
  home, services, whatWeDo, howWeWork, partner, academy, about, clients, quote,
  contact, contactPage, storiesPage, stories, storyArt,
} from './content';
function Clients() {
  return (
    <Section>
      <Head eyebrow={home.trusted.eyebrow} title="Schools and hospitals that eat with us" center />
      <div className="sg-clients">{clients.map((c) => <span className="sg-client" key={c}>{c}</span>)}</div>
      {quote.text && (
        <figure className="sg-quote">
          <blockquote>“{quote.text}”</blockquote>
          <figcaption>{quote.name}{quote.role ? `, ${quote.role}` : ''}</figcaption>
        </figure>
      )}
    </Section>
  );
}
// ---------------------------------------------------------------- HOME
export function Home() {
  usePageMeta(home.meta);
  const h = home.hero;
  return (
    <>
      <Section>
        <div className="sg-split">
          <div>
            <div className="sg-eyebrow">{h.eyebrow}</div>
            <h1 className="sg-h1">{h.title}</h1>
            <p className="sg-lead">{h.lead}</p>
            <div className="sg-actions">
              <Button to="/what-we-do">Our services</Button>
              <Button to="/contact" variant="outline">Talk to us</Button>
            </div>
            <p className="sg-proof">{h.proof}</p>
          </div>
          <Art name="hero" />
        </div>
      </Section>
      <Section tone="white">
        <div className="sg-split sg-split--reverse">
          <div>
            <div className="sg-eyebrow">{home.intro.eyebrow}</div>
            <h2 className="sg-h2">{home.intro.title}</h2>
            <div className="sg-stack sg-lead">{home.intro.text.map((t) => <p key={t}>{t}</p>)}</div>
            <div className="sg-actions"><Link className="sg-link" to="/about">About us →</Link></div>
          </div>
          <Art name="kitchen" />
        </div>
      </Section>
      <Section>
        <Head eyebrow={home.services.eyebrow} title={home.services.title} />
        <div className="sg-grid sg-grid--4">{services.map((s) => <ServiceCard key={s.slug} s={s} />)}</div>
      </Section>
      <Section tone="white">
        <div className="sg-split">
          <div>
            <div className="sg-eyebrow">{home.promise.eyebrow}</div>
            <h2 className="sg-h2">{home.promise.title}</h2>
            <div className="sg-promise">
              {home.promise.items.map((p) => (
                <p key={p.title}><b>{p.title}</b> {p.withLicence || p.text}</p>
              ))}
            </div>
            <div className="sg-actions"><Link className="sg-link" to="/how-we-work">How we work →</Link></div>
          </div>
          <Art name="farmer" />
        </div>
      </Section>
      <PartnerBand title={home.partner.title} text={home.partner.text} />
      <Section>
        <div className="sg-split sg-split--reverse">
          <div>
            <div className="sg-eyebrow">{home.academy.eyebrow}</div>
            <h2 className="sg-h2">{home.academy.title}</h2>
            <p className="sg-lead">{home.academy.text}</p>
            <div className="sg-actions">
              <Button to="/academy">Apply to the Academy</Button>
              <Button to="/academy#jobs" variant="outline">See jobs</Button>
            </div>
          </div>
          <Art name="apprentice" />
        </div>
      </Section>
      <div style={{ background: '#fff' }}><Clients /></div>
    </>
  );
}
// ---------------------------------------------------------------- WHAT WE DO
export function WhatWeDo() {
  usePageMeta(whatWeDo.meta);
  return (
    <>
      <Section>
        <Head as="h1" eyebrow={whatWeDo.eyebrow} title={whatWeDo.title} lead={whatWeDo.lead} />
        <div className="sg-grid sg-grid--2">{services.map((s) => <ServiceCard key={s.slug} s={s} />)}</div>
      </Section>
    </>
  );
}
export function Service() {
  const { slug } = useParams();
  const s = services.find((x) => x.slug === slug);
  usePageMeta(s ? s.meta : null);
  if (!s) return <NotFound />;
  const interest = { schools: 'School meals', hospitals: 'Hospital catering', events: 'Events & corporate', products: 'Products or bulk order' }[s.slug];
  return (
    <>
      <PageHero eyebrow={s.eyebrow} title={s.headline} lead={s.lead} art={s.art}>
        <div className="sg-actions">
          <Button to={`/contact?interest=${encodeURIComponent(interest)}`}>{s.slug === 'products' ? 'Order in bulk' : 'Ask for a quote'}</Button>
          {s.menuPdf && <Button href={s.menuPdf} variant="outline">Sample menu (PDF)</Button>}
        </div>
      </PageHero>
      <Section tone="white">
        {s.products ? (
          <>
            <Head title="Our range" />
            <div className="sg-grid sg-grid--range">
              {s.products.map((p) => (
                <div className="sg-card" key={p.name}>
                  <Art name={productArt(p)} />
                  <div className="sg-card__body">
                    <div className="sg-card__title">{p.name}</div>
                    <p className="sg-muted">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
            {s.whereToBuy && <p className="sg-lead">{s.whereToBuy}</p>}
          </>
        ) : (
          <div className="sg-grid sg-grid--2">
            <div>
              <h2 className="sg-h3" style={{ marginBottom: 16 }}>{s.detailTitle}</h2>
              <Ticks items={s.ticks} />
            </div>
            <div>
              {s.servedArt && <div style={{ marginBottom: 20 }}><Art name={s.servedArt} /></div>}
              <h2 className="sg-h3" style={{ marginBottom: 12 }}>{s.servedTitle}</h2>
              <p className="sg-lead" style={{ marginTop: 0 }}>{s.served}</p>
            </div>
          </div>
        )}
      </Section>
      {s.steps && s.steps.length > 0 && (
        <Section>
          <Head title={s.stepsTitle} />
          <Steps items={s.steps} />
        </Section>
      )}
      <Section tone={s.steps && s.steps.length ? 'white' : undefined}>
        <div className="sg-grid sg-grid--2" style={{ alignItems: 'center' }}>
          <div>
            <h2 className="sg-h2">Talk to the right person</h2>
            <p className="sg-lead">Call, WhatsApp or send a message. We reply within 24 hours.</p>
          </div>
          <Person {...s.person} showContact />
        </div>
      </Section>
    </>
  );
}
// ---------------------------------------------------------------- HOW WE WORK
export function HowWeWork() {
  usePageMeta(howWeWork.meta);
  return (
    <>
      <Section>
        <Head as="h1" eyebrow={howWeWork.eyebrow} title={howWeWork.title} lead={howWeWork.lead} />
        <NetworkModel />
      </Section>
      {howWeWork.sections.map((sec, i) => (
        <Section key={sec.id} id={sec.id} tone={i % 2 === 0 ? 'white' : undefined}>
          <div className={`sg-split${i % 2 ? ' sg-split--reverse' : ''}`}>
            <div>
              <h2 className="sg-h2">{sec.title}</h2>
              <p className="sg-lead">{sec.text}</p>
              {sec.licence && <p className="sg-lead"><b>{sec.licence}</b></p>}
            </div>
            <Art name={sec.art} />
          </div>
        </Section>
      ))}
    </>
  );
}
// ---------------------------------------------------------------- PARTNER
export function Partner() {
  usePageMeta(partner.meta);
  const f = partner.funders;
  return (
    <>
      <PageHero eyebrow={partner.eyebrow} title={partner.title} lead={partner.lead} art="van">
        <div className="sg-actions">
          <Button to={`/contact?interest=${encodeURIComponent('Sarvaguna in my city')}`}>Start a conversation</Button>
          <Button href="#funders" variant="outline">For funders</Button>
        </div>
      </PageHero>
      <Section tone="white">
        <Head title={partner.whoTitle} />
        <div className="sg-grid sg-grid--2">
          {partner.who.map((w) => <div className="sg-panel" key={w.title}><h3>{w.title}</h3><p className="sg-muted">{w.text}</p></div>)}
        </div>
      </Section>
      <Section>
        <Head title={partner.stepsTitle} />
        <Steps items={partner.steps} />
      </Section>
      <Section tone="white" id={f.id}>
        <Head title={f.title} lead={`Where we stand, as of ${f.asOf}.`} />
        <h3 className="sg-h3" style={{ margin: '0 0 16px' }}>How the model works</h3>
        <NetworkModel compact />
        <h3 className="sg-h3" style={{ margin: '40px 0 16px' }}>Our figures</h3>
        <Facts items={f.facts} />
        <h3 className="sg-h3" style={{ margin: '40px 0 16px' }}>{f.goalsTitle}</h3>
        <Facts items={f.goals} />
        <div className="sg-actions">
          {f.profilePdf && <Button href={f.profilePdf}>Download our profile (PDF)</Button>}
          <Button to={`/contact?interest=${encodeURIComponent('Funding or investment')}`} variant={f.profilePdf ? 'outline' : 'primary'}>Talk to us about funding</Button>
          <Link className="sg-link" to="/about#partners" style={{ alignSelf: 'center' }}>Our partners →</Link>
        </div>
      </Section>
    </>
  );
}
// ---------------------------------------------------------------- ACADEMY
export function Academy() {
  usePageMeta(academy.meta);
  const batches = academy.batches.filter((b) => b.when);
  return (
    <>
      <PageHero eyebrow={academy.eyebrow} title={academy.title} lead={academy.lead} art="apprentice">
        <div className="sg-actions">
          <Button to={`/contact?interest=${encodeURIComponent('Academy')}`}>Apply for the next batch</Button>
        </div>
      </PageHero>
      <Section tone="white">
        <div className="sg-grid sg-grid--2">
          <div>
            <h2 className="sg-h3" style={{ marginBottom: 16 }}>{academy.learnTitle}</h2>
            <Ticks items={academy.learn} />
            {academy.whoCanApply && <p className="sg-lead"><b>Who can apply:</b> {academy.whoCanApply}</p>}
            {academy.stipend && <p className="sg-lead"><b>Stipend:</b> {academy.stipend}</p>}
          </div>
          <div>
            <h2 className="sg-h3" style={{ marginBottom: 8 }}>{academy.batchesTitle}</h2>
            <div className="sg-rows">
              {batches.map((b) => (
                <div className="sg-row" key={b.name}><b>{b.name}</b><span>{b.when}</span><span className="sg-muted">{b.note}</span></div>
              ))}
            </div>
          </div>
        </div>
      </Section>
      <Section id="jobs">
        <Head title={academy.jobsTitle} />
        {academy.jobs.length ? (
          <div className="sg-grid sg-grid--2">
            {academy.jobs.map((j) => <div className="sg-panel" key={j.title}><h3>{j.title}</h3><p className="sg-muted">{j.type}</p><p>{j.text}</p></div>)}
          </div>
        ) : (
          <p className="sg-lead" style={{ marginTop: 0 }}>{academy.noJobs}</p>
        )}
      </Section>
    </>
  );
}
// ---------------------------------------------------------------- ABOUT
export function About() {
  usePageMeta(about.meta);
  return (
    <>
      <Section>
        <div className="sg-split">
          <div>
            <div className="sg-eyebrow">{about.eyebrow}</div>
            <h1 className="sg-h1">{about.title}</h1>
            <div className="sg-stack sg-lead">{about.story.map((t) => <p key={t}>{t}</p>)}</div>
          </div>
          <Art name="kitchen" />
        </div>
      </Section>
      <Section tone="white">
        <div className="sg-grid sg-grid--2">
          <div><h2 className="sg-h3" style={{ marginBottom: 10 }}>{about.nameTitle}</h2><p className="sg-lead" style={{ marginTop: 0 }}>{about.name}</p></div>
          <div><h2 className="sg-h3" style={{ marginBottom: 10 }}>Our vision</h2><p className="sg-lead" style={{ marginTop: 0 }}>{about.vision}</p></div>
        </div>
      </Section>
      <Section id="team">
        <Head title={about.teamTitle} />
        <div className="sg-team">{about.team.map((p) => <Person key={p.name} {...p} />)}</div>
      </Section>
      <Section tone="white" id="partners">
        <Head title={about.partnersTitle} />
        <div className="sg-grid sg-grid--3 sg-partners">
          {about.partners.map((g) => (
            <div key={g.group}>
              <h3>{g.group}</h3>
              <ul>{g.items.map(([n, w]) => <li key={n}>{n} <span>· {w}</span></li>)}</ul>
            </div>
          ))}
        </div>
      </Section>
      <Section>
        <div className="sg-narrow">
          <h2 className="sg-h3" style={{ marginBottom: 10 }}>{about.groupTitle}</h2>
          <p className="sg-lead" style={{ marginTop: 0 }}>{about.group}</p>
        </div>
      </Section>
    </>
  );
}
// ---------------------------------------------------------------- CONTACT
export function ContactPage() {
  usePageMeta(contactPage.meta);
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  return (
    <Section>
      <div className="sg-grid sg-grid--2" style={{ alignItems: 'start', gap: 48 }}>
        <div>
          <Head as="h1" eyebrow={contactPage.eyebrow} title={contactPage.title} lead={contactPage.lead} />
          <ContactForm defaultInterest={params.get('interest') || ''} />
        </div>
        <div className="sg-stack">
          <div className="sg-panel">
            <h3>Call or WhatsApp</h3>
            <p><a className="sg-link" href={contact.phoneHref}>{contact.phone}</a> · <a className="sg-link" href={contact.whatsappHref} target="_blank" rel="noreferrer">WhatsApp</a></p>
            <p style={{ marginTop: 8 }}><a className="sg-link" href={`mailto:${contact.email}`}>{contact.email}</a></p>
          </div>
          <div className="sg-panel">
            <h3>{contact.kitchenLabel}</h3>
            <p>{contact.kitchen}</p>
            <p className="sg-muted" style={{ marginTop: 8 }}>{contact.hours.join(' · ')}</p>
          </div>
          <iframe className="sg-map" title="Map of our central kitchen in Budibazaar, Pokhara" src={contact.mapEmbed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </div>
    </Section>
  );
}
// ---------------------------------------------------------------- STORIES
export function Stories() {
  usePageMeta(storiesPage.meta);
  return (
    <Section>
      <Head as="h1" eyebrow={storiesPage.eyebrow} title={storiesPage.title} />
      <div className="sg-grid sg-grid--3">
        {stories.map((s) => (
          <Link className="sg-card" to={`/stories/${s.slug}`} key={s.slug}>
            <Art name={storyArt[s.category] || 'thali'} />
            <div className="sg-card__body">
              <span className="sg-tag">{s.category}</span>
              <div className="sg-card__title">{s.title}</div>
              <p className="sg-muted">{s.excerpt}</p>
              <span className="sg-card__more">Read →</span>
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}
export function Story() {
  const { slug } = useParams();
  const s = stories.find((x) => x.slug === slug);
  usePageMeta(s ? { title: `${s.title} | Sarvaguna`, description: s.excerpt } : null);
  if (!s) return <NotFound />;
  return (
    <Section>
      <div className="sg-prose">
        <Link className="sg-link" to="/stories">← All stories</Link>
        <div className="sg-tag" style={{ marginTop: 28 }}>{s.category}</div>
        <h1 className="sg-h2" style={{ marginTop: 10 }}>{s.title}</h1>
        <p className="sg-lead">{s.excerpt}</p>
        <div style={{ margin: '32px 0' }}><Art name={storyArt[s.category] || 'thali'} /></div>
        {s.body.map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </Section>
  );
}
// ---------------------------------------------------------------- 404
export function NotFound() {
  usePageMeta({ title: 'Page not found | Sarvaguna', description: '' });
  return (
    <Section>
      <div className="sg-narrow">
        <h1 className="sg-h1">This page is not on the menu.</h1>
        <p className="sg-lead">The link may be old. Try the home page or tell us what you were looking for.</p>
        <div className="sg-actions"><Button to="/">Home</Button><Button to="/contact" variant="outline">Contact</Button></div>
      </div>
    </Section>
  );
}
